import { useState, useCallback, useEffect, useRef } from 'react';
import { Head, Link } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { route } from '../../utils/route';
import { getCSRFToken } from '../../utils/csrf';
import { dataLayerPushAsync } from '../../utils/tracking';
import { SuggestedItems } from '../../components/discount/SuggestedItems';
import type { SuggestedItem } from '../../components/discount/SuggestedItems';

interface DeliveryDate {
  value: string;
  day_name: string;
  date: string;
  shift: string;
}

interface DeliverySlot {
  dayName: string;
  date: string;
  shiftStart: string | null;
  shiftEnd: string | null;
}

interface DiscountData {
  code: string;
  discount: number;
  limit: number | null;
  neighborhood: string | null;
  once_per_user: boolean;
  per_user: boolean;
  expires_at: string;
  is_free_delivery: boolean;
  location_address: string | null;
  has_suggested_cart: boolean;
  is_referral_gift: boolean;
}

interface FeaturedProduct {
  id: number;
  title: string;
  image_url: string;
  webp_image_url: string;
  has_image: boolean;
  format_name: string;
  price: number;
}

interface ReferralGiftInfo {
  product_name: string;
  format_name: string;
  content_description: string;
  quantity: number;
  image_url: string;
  webp_image_url: string;
  has_image: boolean;
  price: number;
  description: string;
}

interface BarrioCampaignShare {
  shareUrl: string;
  rewardAmount: number;
}

interface DiscountShareProps {
  discount: DiscountData;
  deliverySlot: DeliverySlot | null;
  daysUntilExpiration: number;
  hoursUntilExpiration: number;
  deliveryDates: DeliveryDate[] | null;
  deliveryAddress: string | null;
  restrictedCategories: string[] | null;
  excludedProductNames: string[];
  featuredProducts: FeaturedProduct[];
  suggestedItems: SuggestedItem[];
  isAlreadyApplied: boolean;
  alreadyUsedByUser: boolean;
  notAssignedToUser: boolean;
  currentUserEmail: string | null;
  isAuthenticated: boolean;
  currentUrl: string;
  referralGift?: ReferralGiftInfo | null;
  referrerName?: string | null;
  isReferralReward?: boolean;
  barrioCampaignShare?: BarrioCampaignShare | null;
  isExpired?: boolean;
  isExhausted?: boolean;
  expiredAgo?: string | null;
  autoApply?: boolean;
}

// ── Hooks ──

function useCountdown(expiresAt: string) {
  const calcRemaining = useCallback(() => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return { hours: 0, minutes: 0, seconds: 0, expired: true };
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { hours, minutes, seconds, expired: false };
  }, [expiresAt]);

  const [remaining, setRemaining] = useState(calcRemaining);

  useEffect(() => {
    const interval = setInterval(() => setRemaining(calcRemaining()), 1000);
    return () => clearInterval(interval);
  }, [calcRemaining]);

  return remaining;
}

// ── Small components ──

function CountdownDigit({ value, label }: { value: number; label: string }) {
  const display = String(value).padStart(2, '0');
  return (
    <div className="flex flex-col items-center">
      <div className="relative flex gap-[3px]">
        {display.split('').map((d, i) => (
          <span
            key={`${i}-${d}`}
            className="flex h-[52px] w-[36px] items-center justify-center rounded-lg bg-gray-900 text-[28px] font-bold tabular-nums text-white shadow-md md:h-[60px] md:w-[42px] md:text-[32px]"
          >
            {d}
          </span>
        ))}
      </div>
      <span className="mt-1.5 text-[11px] font-medium uppercase tracking-wider text-gray-400">
        {label}
      </span>
    </div>
  );
}

function CountdownSeparator() {
  return (
    <div className="flex flex-col items-center justify-center pb-5">
      <span className="text-[28px] font-bold leading-none text-gray-300 md:text-[32px]">:</span>
    </div>
  );
}

// ── Utilities ──

const loadConfetti = (): Promise<typeof import('canvas-confetti').default> => {
  return new Promise((resolve, reject) => {
    if ((window as any).confetti) {
      resolve((window as any).confetti);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
    script.onload = () => resolve((window as any).confetti);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const formatLimit = (limit: number) => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(limit);
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatPrice = (price: number) => {
  return price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ── Icon components ──

function CalendarIcon() {
  return (
    <svg className="h-[18px] w-[18px] flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function ClockIcon({ className = 'text-gray-400' }: { className?: string }) {
  return (
    <svg className={`h-[18px] w-[18px] flex-shrink-0 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function LocationIcon() {
  return (
    <svg className="h-[18px] w-[18px] flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg className="h-[18px] w-[18px] flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function TruckIcon() {
  return (
    <svg className="h-[18px] w-[18px] flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="1" y="3" width="15" height="13" rx="2" ry="2" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

function CheckIcon({ className = 'h-[18px] w-[18px]' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg className="h-[18px] w-[18px] animate-spin" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4 31.4" strokeLinecap="round" />
    </svg>
  );
}

// ── Main component ──

export default function Share({
  discount,
  deliverySlot,
  daysUntilExpiration,
  hoursUntilExpiration,
  deliveryDates,
  deliveryAddress,
  restrictedCategories,
  excludedProductNames,
  featuredProducts,
  suggestedItems,
  isAlreadyApplied,
  alreadyUsedByUser,
  notAssignedToUser,
  currentUserEmail,
  isAuthenticated,
  currentUrl,
  referralGift,
  referrerName,
  isReferralReward,
  barrioCampaignShare,
  isExpired,
  isExhausted,
  expiredAgo,
  autoApply,
}: DiscountShareProps) {
  const [isApplying, setIsApplying] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showExpiredIntro, setShowExpiredIntro] = useState(!!isExpired || !!isExhausted);
  const [expiredIntroPhase, setExpiredIntroPhase] = useState<'rain' | 'text' | 'fade'>('rain');
  const [refCopied, setRefCopied] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [suggestedCartCode, setSuggestedCartCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState<string | null>(null);
  const trackedView = useRef(false);

  const isNearbyOffer = !!deliverySlot;
  const isReferralGift = discount.is_referral_gift && !!referralGift;
  const showCountdown = hoursUntilExpiration < 24;
  const countdown = useCountdown(discount.expires_at);

  // Expired intro animation sequence
  useEffect(() => {
    if (!showExpiredIntro) return;
    const t1 = setTimeout(() => setExpiredIntroPhase('text'), 1800);
    const t2 = setTimeout(() => setExpiredIntroPhase('fade'), 5000);
    const t3 = setTimeout(() => setShowExpiredIntro(false), 5800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [showExpiredIntro]);

  // Track view on mount
  useEffect(() => {
    if (trackedView.current) return;
    trackedView.current = true;

    if (isNearbyOffer) {
      dataLayerPushAsync({
        event: 'nearby_offer_viewed',
        discount_percent: discount.discount,
      });
    } else if (isReferralGift) {
      dataLayerPushAsync({
        event: 'referral_link_opened',
        referral_code: discount.code,
      });
    }
  }, []);

  const titleSuffix = discount.neighborhood ? ` para ${discount.neighborhood}` : '';

  // Meta content
  const pageTitle = isReferralGift
    ? `${referrerName || 'Un amigo'} te quiere regalar algo | Abasto Virtual`
    : isNearbyOffer
      ? 'Oferta especial cerca tuyo | Abasto Virtual'
      : discount.is_free_delivery
        ? `¡Envío gratis!${titleSuffix} | Abasto Virtual`
        : `${discount.discount}% de descuento${titleSuffix} | Abasto Virtual`;
  const limitText = !discount.is_free_delivery && discount.limit ? ` (tope ${formatLimit(discount.limit)})` : '';
  const metaDescription = isReferralGift
    ? `${referrerName || 'Un amigo'} te quiere regalar ${referralGift?.description} GRATIS en Abasto Virtual.`
    : discount.is_free_delivery
      ? `¡Tenés envío gratis en tu próxima compra${titleSuffix} en Abasto Virtual!`
      : `Tenés un descuento especial del ${discount.discount}%${limitText} en Abasto Virtual${titleSuffix}.`;
  const ogTitle = isReferralGift
    ? `🎁 ${referrerName || 'Un amigo'} te quiere regalar ${referralGift?.description} GRATIS`
    : discount.is_free_delivery
      ? `🚚 ¡Envío gratis${titleSuffix} en AbastoVirtual!`
      : `🎁 ${discount.discount}% de descuento en AbastoVirtual${titleSuffix}`;
  const ogDescription = isReferralGift
    ? `Recibí ${referralGift?.description} GRATIS en tu primer pedido en Abasto Virtual.`
    : discount.is_free_delivery
      ? `¡Tenés envío gratis para tu próxima compra de frutas y verduras${titleSuffix}!`
      : `Tenés un descuento especial del ${discount.discount}%${limitText} para tu próxima compra de frutas y verduras.`;

  const fireConfetti = useCallback(async () => {
    try {
      const confetti = await loadConfetti();
      const colors = ['#16a34a', '#15803d', '#22c55e', '#4ade80', '#fbbf24', '#f59e0b'];

      confetti({ particleCount: 150, spread: 100, origin: { y: 0.6 }, colors });

      const end = Date.now() + 2500;
      const interval = setInterval(() => {
        if (Date.now() > end) { clearInterval(interval); return; }
        confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0, y: 0.6 }, colors });
        confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1, y: 0.6 }, colors });
      }, 40);

      setTimeout(() => confetti({ particleCount: 80, spread: 120, origin: { y: 0.5 } }), 300);
      setTimeout(() => confetti({ particleCount: 60, spread: 100, origin: { y: 0.7 } }), 600);
    } catch {}
  }, []);

  const handleApply = async () => {
    if (isApplying) return;

    // For per_user discounts, redirect to login if not authenticated
    if (discount.per_user && !isAuthenticated) {
      const returnUrl = currentUrl + (currentUrl.includes('?') ? '&' : '?') + 'apply';
      window.location.href = `/login?returnTo=${encodeURIComponent(returnUrl)}`;
      return;
    }

    setIsApplying(true);
    setError(null);

    try {
      const response = await fetch(route('api.discount.apply', { code: discount.code }), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
          Accept: 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (data.success) {
        setCartItemCount(data.cartItemCount || 0);
        if (data.suggestedCartCode) {
          setSuggestedCartCode(data.suggestedCartCode);
        }

        if (isNearbyOffer) {
          dataLayerPushAsync({
            event: 'nearby_offer_applied',
            discount_percent: discount.discount,
            cart_items_loaded: data.cartItemCount || 0,
          });
        } else if (isReferralGift) {
          dataLayerPushAsync({
            event: 'referral_applied',
            referral_code: discount.code,
          });
        }

        fireConfetti();
        setTimeout(() => setShowOverlay(true), 1200);
      } else {
        setError(data.message || 'Hubo un error al aplicar el descuento.');
        setIsApplying(false);
      }
    } catch {
      setError('Error de conexión. Por favor, intentá de nuevo.');
      setIsApplying(false);
    }
  };

  // Auto-apply on mount when ?apply is present (e.g., after login redirect)
  const autoApplied = useRef(false);
  useEffect(() => {
    if (autoApply && isAuthenticated && !autoApplied.current) {
      autoApplied.current = true;
      handleApply();
    }
  }, [autoApply, isAuthenticated]);

  const handleShare = async () => {
    const shareTitle = discount.is_free_delivery
      ? `¡Envío gratis en Abasto Virtual!`
      : `${discount.discount}% de descuento en Abasto Virtual`;
    const shareText = discount.is_free_delivery
      ? `¡Tenés envío gratis para tu próxima compra de frutas y verduras en Abasto Virtual!`
      : `Tenés un ${discount.discount}% de descuento para tu próxima compra de frutas y verduras en Abasto Virtual.`;

    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: currentUrl });
      } catch (err: any) {
        if (err.name !== 'AbortError') copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert('No se pudo copiar el link. URL: ' + currentUrl);
    }
  };

  const handleSelectDeliveryDate = async (dateValue: string) => {
    setSelectedDeliveryDate(dateValue);
    try {
      await fetch(route('discount.delivery-preference'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ delivery_date: dateValue }),
      });
    } catch {}
  };

  // Expired: server-side or live countdown hit zero
  const isDiscountExpired = isExpired || isExhausted || (showCountdown && countdown.expired);

  // ── Personal discount opened by a user it wasn't assigned to ──
  // (wrong account, forwarded link, or a freshly registered account)
  if (notAssignedToUser) {
    return (
      <MainLayout>
        <Head title="Promoción personal">
          <meta name="robots" content="noindex" />
        </Head>
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-slate-50 pb-16 pt-6">
          <div className="mx-auto max-w-[500px] px-4">
            <div className="mt-6 rounded-2xl bg-white p-6 text-center shadow-lg md:p-8">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-amber-100">
                <UserIcon />
              </div>
              <h1 className="mb-2 text-2xl font-bold text-gray-900">Esta promo es personal</h1>
              <p className="mb-4 text-base text-gray-500">
                Esta promoción está asociada a otra cuenta y no se puede transferir.
                Si tenés más de una cuenta, iniciá sesión con la cuenta a la que te la enviamos.
              </p>

              {/* Diagnostic info — helps support when the user sends a screenshot.
                  We never show the account the promo IS assigned to. */}
              <div className="mb-6 rounded-xl bg-slate-50 p-4 text-left text-[13px] text-gray-600">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Si necesitás ayuda, mandanos estos datos
                </div>
                {currentUserEmail && (
                  <div className="flex items-center justify-between gap-3 border-b border-gray-200 py-1.5">
                    <span className="text-gray-400">Cuenta actual</span>
                    <span className="font-medium text-gray-700 break-all">{currentUserEmail}</span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 py-1.5">
                  <span className="text-gray-400">Código</span>
                  <span className="font-mono font-medium text-gray-700">{discount.code}</span>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <a
                  href="/productos"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-500 px-7 py-3.5 text-base font-semibold text-white transition-all hover:bg-green-600 hover:text-white hover:no-underline"
                >
                  Ver productos
                </a>
                <a
                  href={route('logout')}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-gray-100 px-7 py-3.5 text-base font-semibold text-gray-700 transition-all hover:bg-gray-200 hover:text-gray-700 hover:no-underline"
                >
                  Cerrar sesión e ingresar con otra cuenta
                </a>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // ── Already used by this user ──
  if (alreadyUsedByUser) {
    return (
      <MainLayout>
        <Head title="Descuento ya utilizado">
          <meta name="robots" content="noindex" />
        </Head>
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-slate-50 pb-16 pt-6">
          <div className="mx-auto max-w-[500px] px-4">
            <div className="mt-6 rounded-2xl bg-white p-6 text-center shadow-lg md:p-8">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                <CheckIcon className="h-10 w-10 text-green-500" />
              </div>
              <h1 className="mb-2 text-2xl font-bold text-gray-900">Ya usaste este descuento</h1>
              <p className="mb-8 text-base text-gray-500">
                Este descuento ya fue aplicado en un pedido. Podés explorar nuestros productos para tu próxima compra.
              </p>
              <a
                href="/productos"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-500 px-7 py-3.5 text-base font-semibold text-white transition-all hover:bg-green-600 hover:text-white hover:no-underline"
              >
                Ver productos
              </a>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // ── Referral gift page ──
  if (isReferralGift && referralGift) {
    return (
      <MainLayout>
        <Head title={pageTitle}>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="description" content={metaDescription} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={currentUrl} />
          <meta property="og:title" content={ogTitle} />
          <meta property="og:description" content={ogDescription} />
          <meta property="og:image" content={referralGift.has_image ? referralGift.image_url : '/appicons/og-image.jpg'} />
          <meta property="og:locale" content="es_AR" />
          <meta property="og:site_name" content="Abasto Virtual" />
        </Head>

        <div className="min-h-screen bg-gradient-to-br from-amber-50 to-green-50 pb-16 pt-6">
          <div className="mx-auto max-w-[500px] px-4">
            <div className="mt-6 rounded-2xl bg-white p-6 text-center shadow-lg md:p-8">
              {/* Gift icon */}
              <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-amber-100">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-10 w-10 text-amber-600">
                  <polyline points="20 12 20 22 4 22 4 12" />
                  <rect x="2" y="7" width="20" height="5" />
                  <line x1="12" y1="22" x2="12" y2="7" />
                  <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                  <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                </svg>
              </div>

              <p className="mb-1 text-sm font-medium text-amber-700">
                {isReferralReward
                  ? '¡Tu amigo hizo su primer pedido!'
                  : referrerName ? `${referrerName} te quiere regegar` : 'Tenés un regalo'}
              </p>

              <h1 className="mb-2 text-2xl font-bold text-gray-900 md:text-[26px]">
                {referralGift.description} GRATIS
              </h1>

              <p className="mb-6 text-sm text-gray-500">
                {isReferralReward
                  ? 'Como agradecimiento por recomendarnos, es tuyo'
                  : 'en tu primer pedido en Abasto Virtual'}
              </p>

              {/* Free delivery badge for new customers */}
              {!isReferralReward && (
                <div className="mb-6 flex items-center justify-center gap-2 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-800">
                  <svg className="h-5 w-5 flex-shrink-0 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="3" width="15" height="13" rx="2" ry="2" />
                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                    <circle cx="5.5" cy="18.5" r="2.5" />
                    <circle cx="18.5" cy="18.5" r="2.5" />
                  </svg>
                  <span>Además, tu primer envío es <strong>gratis</strong></span>
                </div>
              )}

              {/* Product image */}
              {referralGift.has_image && (
                <div className="mx-auto mb-6 flex h-40 w-40 items-center justify-center overflow-hidden rounded-2xl bg-gray-50 p-3">
                  <picture>
                    <source srcSet={referralGift.webp_image_url} type="image/webp" />
                    <img
                      src={referralGift.image_url}
                      alt={referralGift.product_name}
                      className="h-full w-full object-contain"
                    />
                  </picture>
                </div>
              )}

              {/* Gift details card */}
              <div className="mb-6 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 p-5 text-white">
                <div className="text-lg font-bold">{referralGift.product_name}</div>
                <div className="mt-1 text-sm opacity-90">{referralGift.description}</div>
                <div className="mt-3 border-t border-white/30 pt-3">
                  <span className="text-sm opacity-80">Valor: </span>
                  <span className="text-lg font-bold line-through opacity-70">
                    ${referralGift.price.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </span>
                  <span className="ml-2 rounded-full bg-white/20 px-3 py-1 text-sm font-bold">GRATIS</span>
                </div>
              </div>

              {/* How it works */}
              {!isReferralReward && (
                <div className="mb-6 rounded-xl bg-slate-50 p-5 text-left">
                  <div className="mb-3 text-sm font-semibold text-gray-700">¿Cómo funciona?</div>
                  <ol className="space-y-2.5 text-sm text-gray-600">
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700">1</span>
                      <span>Hacé tu primer pedido en Abasto Virtual</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700">2</span>
                      <span>Se agrega {referralGift.description} gratis a tu carrito</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700">3</span>
                      <span>Recibís todo directo en tu casa</span>
                    </li>
                  </ol>
                </div>
              )}

              {/* Expiration */}
              {!showCountdown && (
                <div className="mb-6 flex items-center justify-center gap-2 text-sm text-gray-500">
                  <ClockIcon />
                  <span>
                    {daysUntilExpiration > 0
                      ? <>Válido por <strong>{daysUntilExpiration} {daysUntilExpiration === 1 ? 'día' : 'días'}</strong> más</>
                      : <><strong>¡Vence hoy!</strong></>
                    }
                  </span>
                </div>
              )}

              {/* CTA */}
              {isAlreadyApplied ? (
                <>
                  <div className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-green-500 bg-green-50 px-6 py-3.5 text-base font-semibold text-green-700">
                    <CheckIcon />
                    Regalo aplicado
                  </div>
                  <Link
                    href={route('cart.review')}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-green-500 px-6 py-3.5 text-base font-semibold text-white transition-all hover:bg-green-600 hover:text-white hover:no-underline"
                  >
                    <CartIcon />
                    Ver mi carrito
                  </Link>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleApply}
                    disabled={isApplying}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 px-6 py-3.5 text-base font-semibold text-white transition-all hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isApplying ? (
                      <>
                        <SpinnerIcon />
                        Aplicando...
                      </>
                    ) : (
                      '¡Sí, lo quiero!'
                    )}
                  </button>

                  {error && (
                    <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[15px] text-red-700">
                      {error}
                    </div>
                  )}

                  {!isAuthenticated && (
                    <p className="mt-4 text-[13px] text-gray-400">
                      Al aceptar, te vamos a pedir que crees una cuenta para poder hacerte el envío.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Success Overlay */}
        <div
          className={`fixed inset-0 z-[9998] flex items-center justify-center bg-white/85 backdrop-blur-sm transition-all duration-500 ${
            showOverlay ? 'visible opacity-100' : 'invisible opacity-0'
          }`}
        >
          <div
            className={`px-6 py-10 text-center transition-transform duration-400 ${
              showOverlay ? 'scale-100' : 'scale-75'
            }`}
            style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
          >
            <div
              className="mx-auto mb-6 flex h-[100px] w-[100px] items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-amber-600"
              style={{
                animation: showOverlay ? 'pop-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both' : 'none',
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-[50px] w-[50px] text-white">
                <polyline points="20 12 20 22 4 22 4 12" />
                <rect x="2" y="7" width="20" height="5" />
                <line x1="12" y1="22" x2="12" y2="7" />
                <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
              </svg>
            </div>

            <h2
              className="mb-3 text-[28px] font-bold text-amber-800"
              style={{ animation: showOverlay ? 'fade-up 0.4s ease 0.3s both' : 'none' }}
            >
              ¡Regalo activado!
            </h2>
            <p
              className="mb-6 text-base text-gray-500"
              style={{ animation: showOverlay ? 'fade-up 0.4s ease 0.4s both' : 'none' }}
            >
              {referralGift.description} ya está en tu carrito.
            </p>

            {suggestedCartCode ? (
              <div
                className="mb-6 rounded-xl bg-green-50 p-5 text-center"
                style={{ animation: showOverlay ? 'fade-up 0.4s ease 0.5s both' : 'none' }}
              >
                <p className="mb-1 text-sm font-semibold text-green-800">
                  Preparamos un carrito para vos
                </p>
                <p className="mb-4 text-xs text-green-600">
                  Una selección de productos populares para que arranques tu primer pedido.
                </p>
                <div className="flex flex-col gap-2.5">
                  <a
                    href={`/carrito?sc=${suggestedCartCode}`}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-green-700 hover:text-white hover:no-underline"
                  >
                    <CartIcon />
                    Ver carrito sugerido
                  </a>
                  <Link
                    href={route('products.index')}
                    className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700 hover:no-underline"
                  >
                    Prefiero elegir yo
                  </Link>
                </div>
              </div>
            ) : (
              <div
                className="flex flex-col gap-3"
                style={{ animation: showOverlay ? 'fade-up 0.4s ease 0.5s both' : 'none' }}
              >
                <Link
                  href={route('cart.review')}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-7 py-3.5 text-base font-semibold text-white transition-all hover:bg-amber-600 hover:text-white hover:no-underline"
                >
                  <CartIcon />
                  Ver mi carrito
                </Link>
                <Link
                  href={route('products.index')}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-gray-100 px-7 py-3.5 text-base font-semibold text-gray-700 transition-all hover:bg-gray-200 hover:text-gray-700 hover:no-underline"
                >
                  Explorar productos
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Keyframe animations */}
        <style>{`
          @keyframes pop-in {
            0% { transform: scale(0); }
            100% { transform: scale(1); }
          }
          @keyframes fade-up {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </MainLayout>
    );
  }

  // ── Main discount page ──

  const shiftRange = deliverySlot?.shiftStart && deliverySlot?.shiftEnd
    ? `entre ${deliverySlot.shiftStart} y ${deliverySlot.shiftEnd}`
    : '';

  return (
    <MainLayout>
      <Head title={pageTitle}>
        <meta name="robots" content="noindex, nofollow" />
        {isDiscountExpired && (
          <link rel="preload" as="image" href="/img/expired-promo-bg.webp" />
        )}
        <meta name="description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content="/appicons/og-image.jpg" />
        <meta property="og:locale" content="es_AR" />
        <meta property="og:site_name" content="Abasto Virtual" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content="/appicons/og-image.jpg" />
      </Head>

      <div className="min-h-screen bg-gradient-to-br from-green-50 to-slate-50 pb-24 pt-6 lg:pb-16">
        <div className="mx-auto max-w-[500px] px-4 lg:max-w-[960px]">
          <div className="mt-6 lg:mt-0 lg:grid lg:grid-cols-[1fr_1fr] lg:gap-6">
          {/* Left column: sell the offer */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-lg lg:flex lg:flex-col">
            {/* Hero discount badge */}
            {discount.is_free_delivery ? (
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 px-6 py-10 text-center text-white md:px-8 md:py-12">
                <div className="text-5xl leading-tight md:text-[56px]">🚚</div>
                <div className="mt-2 text-xl font-bold uppercase tracking-wider">¡Envío gratis!</div>
                <div className="mt-1 text-sm opacity-80">El costo de envío va por nuestra cuenta</div>
              </div>
            ) : (
              <div className="px-6 py-8 text-center text-white md:px-8 lg:py-10" style={{ background: 'linear-gradient(135deg, #1EAA25 0%, #178F1E 100%)' }}>
                <div className="text-6xl font-extrabold leading-none tracking-tight md:text-7xl">{Math.round(Number(discount.discount))}%</div>
                <div className="mt-1 text-sm font-medium uppercase tracking-wider opacity-80">de descuento</div>
                {discount.limit && (
                  <div className="mt-3 inline-block rounded-full bg-white/15 px-4 py-1.5 text-sm font-medium backdrop-blur-sm">
                    Tope: {formatLimit(discount.limit)}
                  </div>
                )}
              </div>
            )}

            <div className="p-6 md:p-8 lg:flex lg:flex-1 lg:flex-col">
            {/* Expired banner */}
            {isDiscountExpired && (
              <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <ClockIcon className="text-amber-500" />
                <span>
                  {isExhausted
                    ? 'Este descuento ya fue utilizado por todos los usuarios disponibles.'
                    : expiredAgo
                      ? <>Esta promo expiró <strong>{expiredAgo}</strong>. Pero podés explorar nuestros productos.</>
                      : 'Esta promo acaba de expirar. Pero podés explorar nuestros productos.'}
                </span>
              </div>
            )}

            {/* Headline */}
            <h1 className="mb-4 text-center text-xl font-bold text-gray-900 lg:text-left lg:text-[22px]">
              {discount.neighborhood
                ? `Frutas y verduras frescas en ${discount.neighborhood}`
                : isNearbyOffer
                  ? 'Frutas y verduras frescas a tu puerta'
                  : discount.is_free_delivery
                    ? 'Recibí tu pedido sin costo de envío'
                    : 'Ahorrá en frutas y verduras frescas'}
            </h1>

            {/* Benefits checklist */}
            <ul className="mb-5 space-y-2.5 text-left">
              <li className="flex items-center gap-2.5 text-sm text-gray-700">
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                  <CheckIcon className="h-3 w-3 text-green-600" />
                </span>
                {discount.is_free_delivery
                  ? <span>Envío <strong>gratis</strong> en tu próxima compra</span>
                  : <span><strong>{Math.round(Number(discount.discount))}% off</strong> en tu pedido{discount.limit ? ` (tope ${formatLimit(discount.limit)})` : ''}</span>
                }
              </li>
              {deliveryDates && deliveryDates.length > 0 && !isNearbyOffer && (discount.neighborhood || deliveryAddress) && (
                <li className="flex items-center gap-2.5 text-sm text-gray-700">
                  <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                    <CheckIcon className="h-3 w-3 text-green-600" />
                  </span>
                  <span>Entrega a domicilio en <strong>{discount.neighborhood || deliveryAddress}</strong></span>
                </li>
              )}
              {deliverySlot && (
                <li className="flex items-center gap-2.5 text-sm text-gray-700">
                  <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                    <CheckIcon className="h-3 w-3 text-green-600" />
                  </span>
                  <span>Recibilo <strong>{deliverySlot.dayName} {deliverySlot.date}</strong> {shiftRange}</span>
                </li>
              )}
              <li className="flex items-center gap-2.5 text-sm text-gray-700">
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                  <CheckIcon className="h-3 w-3 text-green-600" />
                </span>
                <span>Productos frescos del <strong>Mercado de Abasto</strong></span>
              </li>
              {!showCountdown && daysUntilExpiration <= 3 && (
                <li className="flex items-center gap-2.5 text-sm text-amber-700">
                  <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                    <ClockIcon className="h-3 w-3 text-amber-600" />
                  </span>
                  <span>
                    {daysUntilExpiration === 0
                      ? <strong>¡Vence hoy!</strong>
                      : <>Quedan <strong>{daysUntilExpiration} {daysUntilExpiration === 1 ? 'día' : 'días'}</strong></>}
                  </span>
                </li>
              )}
            </ul>

            {/* Countdown (for short-lived discounts) */}
            {showCountdown && !countdown.expired && (
              <div className="mb-5 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 px-5 py-5">
                <div className="mb-3 text-center text-[11px] font-semibold uppercase tracking-widest text-amber-400">
                  La oferta expira en
                </div>
                <div className="flex items-center justify-center gap-2">
                  {countdown.hours > 0 && (
                    <>
                      <CountdownDigit value={countdown.hours} label="hs" />
                      <CountdownSeparator />
                    </>
                  )}
                  <CountdownDigit value={countdown.minutes} label="min" />
                  <CountdownSeparator />
                  <CountdownDigit value={countdown.seconds} label="seg" />
                </div>
              </div>
            )}

            {/* Delivery date picker (desktop only, not for fixed-slot discounts) */}
            {deliveryDates && deliveryDates.length > 0 && !isNearbyOffer && (
              <div className="mb-5 hidden rounded-xl bg-gradient-to-br from-amber-50 to-amber-100 p-4 text-left lg:block">
                <div className="mb-2.5 text-[13px] font-semibold text-amber-800">
                  {deliveryAddress
                    ? `Elegí cuándo recibir tu pedido en ${deliveryAddress}`
                    : 'Elegí cuándo recibir tu pedido'}
                </div>
                <div className="flex flex-col gap-1.5">
                  {deliveryDates.map((date, index) => {
                    const isSelected = selectedDeliveryDate
                      ? selectedDeliveryDate === date.value
                      : index === 0;
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelectDeliveryDate(date.value)}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-all hover:shadow-md ${
                          isSelected
                            ? 'border-2 border-green-500 bg-white shadow-md'
                            : 'border-2 border-transparent bg-white/70 cursor-pointer hover:bg-white'
                        }`}
                      >
                        <span className={`font-semibold ${isSelected ? 'text-green-600' : 'text-gray-700'}`}>
                          {date.day_name} {date.date}
                        </span>
                        <span className="text-[13px] text-gray-500">{date.shift}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* CTA (shown here on desktop, hidden on mobile) */}
            <div className="hidden lg:mt-auto lg:block">
              {isDiscountExpired ? (
                <Link
                  href={route('products.index')}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1EAA25] px-6 py-4 text-[17px] font-bold text-white shadow-lg transition-all hover:opacity-90 hover:shadow-xl hover:text-white hover:no-underline"
                >
                  <CartIcon />
                  Explorar productos
                </Link>
              ) : isAlreadyApplied ? (
                <>
                  <div className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-green-500 bg-green-50 px-6 py-3.5 text-base font-semibold text-green-700">
                    <CheckIcon />
                    Descuento aplicado
                  </div>
                  <Link
                    href={route('products.index')}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-[#1EAA25] px-6 py-3.5 text-base font-semibold text-white transition-all hover:opacity-90 hover:text-white hover:no-underline"
                  >
                    <CartIcon />
                    Empezar a comprar
                  </Link>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleApply}
                    disabled={isApplying}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1EAA25] px-6 py-4 text-[17px] font-bold text-white shadow-lg transition-all hover:opacity-90 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isApplying ? (<><SpinnerIcon /> Aplicando...</>) : (
                      discount.is_free_delivery ? 'Comprar con envío gratis' : `Comprar con ${Math.round(Number(discount.discount))}% off`
                    )}
                  </button>
                  {error && (
                    <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[15px] text-red-700">
                      {error}
                    </div>
                  )}
                  <p className="mt-3 text-center text-[12px] text-gray-400">
                    Se guardará automáticamente para tu próxima compra
                  </p>
                </>
              )}
            </div>
          </div>{/* End p-6 inner padding */}
          </div>{/* End left column */}

          {/* Right column: products + share (desktop), details flow below on mobile */}
          <div className="mt-6 space-y-6 lg:mt-0 lg:flex lg:min-h-0 lg:flex-col lg:space-y-0 lg:gap-6">

            {/* Curated suggestions - takes precedence over featured products on desktop */}
            {suggestedItems.length > 0 && !isDiscountExpired && (
              <div className="hidden lg:flex lg:min-h-0 lg:flex-1">
                <SuggestedItems
                  items={suggestedItems}
                  discountCode={discount.code}
                  discountPercent={Number(discount.discount)}
                  isAuthenticated={isAuthenticated}
                  isPerUser={discount.per_user}
                  currentUrl={currentUrl}
                />
              </div>
            )}

            {/* Featured products - prominent on desktop (only when no curated suggestions) */}
            {suggestedItems.length === 0 && !discount.is_free_delivery && featuredProducts.length > 0 && !isDiscountExpired && (
              <div className="hidden rounded-2xl bg-white p-6 shadow-lg lg:block">
                <div className="mb-1 text-[15px] font-bold text-gray-900">Mirá cuánto ahorrás</div>
                <p className="mb-4 text-[13px] text-gray-500">Precios con tu {Math.round(Number(discount.discount))}% aplicado</p>
                <div className="grid grid-cols-3 gap-2.5">
                  {featuredProducts.map((product) => {
                    const discountedPrice = product.price * (1 - discount.discount / 100);
                    const savings = product.price - discountedPrice;
                    return (
                      <div key={product.id} className="flex flex-col rounded-lg border border-gray-100 bg-gray-50/50 p-2.5">
                        <div className="mx-auto mb-2">
                          {product.has_image ? (
                            <img
                              src={product.webp_image_url}
                              alt={product.title}
                              loading="lazy"
                              width={80}
                              height={80}
                              className="aspect-square w-[70px] object-contain"
                            />
                          ) : (
                            <div className="flex aspect-square w-[70px] items-center justify-center rounded bg-gray-100">
                              <i className="fa-solid fa-leaf text-xl text-gray-300" />
                            </div>
                          )}
                        </div>
                        <div className="mb-1 line-clamp-2 text-[11px] font-medium leading-tight text-gray-700">
                          {product.title}
                        </div>
                        <div className="mt-auto">
                          <div className="text-[11px] text-gray-400 line-through">${formatPrice(product.price)}</div>
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-bold text-green-600">${formatPrice(discountedPrice)}</span>
                          </div>
                          <div className="text-[10px] font-medium text-green-600">Ahorrás ${formatPrice(savings)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Barrio campaign share (WhatsApp + copy link) */}
            {barrioCampaignShare && !isDiscountExpired && (
              <ReferralShareSection share={barrioCampaignShare} discount={discount} refCopied={refCopied} setRefCopied={setRefCopied} />
            )}

            {/* Generic share (non-barrio, non-per_user) */}
            {!discount.per_user && !barrioCampaignShare && !isDiscountExpired && (
              <div>
                <button
                  type="button"
                  onClick={handleShare}
                  className={`flex w-full items-center justify-center gap-2 rounded-xl border-2 px-6 py-3 text-sm font-medium transition-all ${
                    copied
                      ? 'border-green-500 bg-green-50 text-green-600'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  {copied ? '¡Link copiado!' : 'Compartir descuento'}
                </button>
              </div>
            )}

            {/* Curated suggestions - mobile only (desktop renders above in right column) */}
            {suggestedItems.length > 0 && !isDiscountExpired && (
              <div className="lg:hidden">
                <SuggestedItems
                  items={suggestedItems}
                  discountCode={discount.code}
                  discountPercent={Number(discount.discount)}
                  isAuthenticated={isAuthenticated}
                  isPerUser={discount.per_user}
                  currentUrl={currentUrl}
                />
              </div>
            )}

            {/* Details - fine print on desktop, full card on mobile */}
            <div className="rounded-xl bg-slate-50 p-5 text-left lg:rounded-2xl lg:bg-white/80 lg:p-5 lg:shadow-sm">
              <details className="hidden lg:block">
                <summary className="cursor-pointer text-[13px] font-medium text-gray-500 hover:text-gray-700">
                  Condiciones del descuento
                </summary>
                <ul className="mt-3 space-y-0">
                  {deliverySlot && (
                    <li className="flex items-center gap-3 border-b border-gray-200 py-2 text-[13px] text-gray-500">
                      <TruckIcon />
                      <span>Entrega: <strong>{deliverySlot.dayName} {deliverySlot.date} {shiftRange}</strong></span>
                    </li>
                  )}
                  {discount.location_address && (
                    <li className="flex items-center gap-3 border-b border-gray-200 py-2 text-[13px] text-gray-500">
                      <LocationIcon />
                      <span>Dirección: <strong>{discount.location_address}</strong></span>
                    </li>
                  )}
                  {(!showCountdown || isDiscountExpired) && (
                    <li className="flex items-center gap-3 border-b border-gray-200 py-2 text-[13px] text-gray-500">
                      <CalendarIcon />
                      <span>{isDiscountExpired ? 'Expiró' : 'Vence'}: <strong>{formatDate(discount.expires_at)}</strong></span>
                    </li>
                  )}
                  {discount.neighborhood && (
                    <li className="flex items-center gap-3 border-b border-gray-200 py-2 text-[13px] text-gray-500">
                      <LocationIcon />
                      <span>Válido solo para <strong>{discount.neighborhood}</strong></span>
                    </li>
                  )}
                  {(discount.per_user || discount.once_per_user) && (
                    <li className="flex items-center gap-3 border-b border-gray-200 py-2 text-[13px] text-gray-500">
                      <UserIcon />
                      <span>{discount.per_user ? <>Promo <strong>exclusiva</strong></> : <>Uso <strong>único</strong></>}</span>
                    </li>
                  )}
                  {!discount.is_free_delivery && restrictedCategories && restrictedCategories.length > 0 && (
                    <li className="flex items-center gap-3 border-b border-gray-200 py-2 text-[13px] text-gray-500">
                      <svg className="h-[18px] w-[18px] flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7h16M4 12h16M4 17h10" /></svg>
                      <span>Válido en: <strong>{restrictedCategories.join(', ')}</strong></span>
                    </li>
                  )}
                  {!discount.is_free_delivery && (
                    <li className="flex items-center gap-3 py-2 text-[13px] text-gray-500">
                      <svg className="h-[18px] w-[18px] flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                      <span>No aplica a formatos <strong>mayoristas</strong> ni a <strong>huevos</strong></span>
                    </li>
                  )}
                </ul>
              </details>
              {/* Mobile: show details expanded */}
              <div className="lg:hidden">
                <div className="mb-4 text-sm font-semibold text-gray-700">
                  {isNearbyOffer ? 'Detalles de la promo' : 'Detalles del descuento'}
                </div>
                <ul className="space-y-0">
                  {deliverySlot && (
                    <li className="flex items-center gap-3 border-b border-gray-200 py-2.5 text-sm text-gray-600">
                      <TruckIcon />
                      <span>Entrega: <strong>{deliverySlot.dayName} {deliverySlot.date} {shiftRange}</strong></span>
                    </li>
                  )}
                  {discount.location_address && (
                    <li className="flex items-center gap-3 border-b border-gray-200 py-2.5 text-sm text-gray-600">
                      <LocationIcon />
                      <span>Dirección: <strong>{discount.location_address}</strong></span>
                    </li>
                  )}
                  {!showCountdown && (
                    <li className="flex items-center gap-3 border-b border-gray-200 py-2.5 text-sm text-gray-600">
                      <CalendarIcon />
                      <span>Vence: <strong>{formatDate(discount.expires_at)}</strong></span>
                    </li>
                  )}
                  {!showCountdown && (
                    daysUntilExpiration > 0 ? (
                      <li className="flex items-center gap-3 border-b border-gray-200 py-2.5 text-sm text-gray-600">
                        <ClockIcon />
                        <span>Quedan <strong>{daysUntilExpiration} {daysUntilExpiration === 1 ? 'día' : 'días'}</strong> para usarlo</span>
                      </li>
                    ) : (
                      <li className="flex items-center gap-3 border-b border-gray-200 py-2.5 text-sm text-amber-600">
                        <ClockIcon className="text-amber-500" />
                        <span><strong>¡Vence hoy!</strong> Quedan {hoursUntilExpiration} {hoursUntilExpiration === 1 ? 'hora' : 'horas'}</span>
                      </li>
                    )
                  )}
                  {discount.neighborhood && (
                    <li className="flex items-center gap-3 border-b border-gray-200 py-2.5 text-sm text-gray-600">
                      <LocationIcon />
                      <span>Válido solo para <strong>{discount.neighborhood}</strong></span>
                    </li>
                  )}
                  {discount.is_free_delivery && (
                    <li className="flex items-center gap-3 border-b border-gray-200 py-2.5 text-sm text-gray-600">
                      <TruckIcon />
                      <span><strong>Envío gratis</strong> incluido</span>
                    </li>
                  )}
                  {(discount.per_user || discount.once_per_user) && (
                    <li className="flex items-center gap-3 border-b border-gray-200 py-2.5 text-sm text-gray-600">
                      <UserIcon />
                      <span>{discount.per_user ? <>Promo <strong>exclusiva</strong> para vos</> : <>Solo podrás usarlo <strong>una vez</strong></>}</span>
                    </li>
                  )}
                  {!discount.is_free_delivery && restrictedCategories && restrictedCategories.length > 0 && (
                    <li className="flex items-center gap-3 border-b border-gray-200 py-2.5 text-sm text-gray-600">
                      <svg className="h-[18px] w-[18px] flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7h16M4 12h16M4 17h10" /></svg>
                      <span>Válido solo en: <strong>{restrictedCategories.join(', ')}</strong></span>
                    </li>
                  )}
                  {!discount.is_free_delivery && (
                    <>
                      {excludedProductNames.length === 0 ? (
                        <li className="flex items-center gap-3 py-2.5 text-sm text-gray-600">
                          <svg className="h-[18px] w-[18px] flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                          <span>No aplica a formatos <strong>mayoristas</strong> ni a <strong>huevos</strong></span>
                        </li>
                      ) : (
                        <>
                          <li className="flex items-center gap-3 border-b border-gray-200 py-2.5 text-sm text-gray-600">
                            <svg className="h-[18px] w-[18px] flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                            <span>No incluye formatos <strong>mayoristas</strong></span>
                          </li>
                          <li className="flex items-center gap-3 py-2.5 text-sm text-gray-600">
                            <svg className="h-[18px] w-[18px] flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                            <span>Productos excluidos: <strong>{excludedProductNames.join(', ')}</strong></span>
                          </li>
                        </>
                      )}
                    </>
                  )}
                </ul>
              </div>
            </div>

            {/* Delivery dates (mobile only, not for fixed-slot discounts) */}
            {deliveryDates && deliveryDates.length > 0 && !isNearbyOffer && (
              <div className="rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 p-5 text-left lg:hidden">
                <div className="mb-4 flex items-center gap-2.5 text-[15px] font-semibold text-amber-800">
                  <svg className="h-[22px] w-[22px] text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="3" width="15" height="13" rx="2" ry="2" />
                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                    <circle cx="5.5" cy="18.5" r="2.5" />
                    <circle cx="18.5" cy="18.5" r="2.5" />
                  </svg>
                  Comprá hoy y te lo llevamos
                </div>
                <div className="flex flex-col gap-2">
                  {deliveryDates.map((date, index) => {
                    const isSelected = selectedDeliveryDate
                      ? selectedDeliveryDate === date.value
                      : index === 0;
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelectDeliveryDate(date.value)}
                        className={`flex w-full items-center justify-between rounded-lg px-3.5 py-3 text-left text-sm transition-all ${
                          isSelected
                            ? 'border-2 border-green-500 bg-white shadow-md'
                            : 'border-2 border-transparent bg-white/70'
                        }`}
                      >
                        <span className={`font-semibold ${isSelected ? 'text-green-600' : 'text-gray-700'}`}>
                          {date.day_name} {date.date}
                        </span>
                        <span className="text-[13px] text-gray-500">{date.shift}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Error display for mobile */}
            {error && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[15px] text-red-700 lg:hidden">
                {error}
              </div>
            )}

            {/* Footer for nearby offers */}
            {isNearbyOffer && (
              <div className="mt-8 border-t border-gray-200 pt-6">
                <p className="m-0 text-[13px] text-gray-400">
                  ¿Tenés alguna duda? Contactanos por el botón de chat o por{' '}
                  <a href="https://wa.me/5493512026384" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">WhatsApp</a>
                </p>
              </div>
            )}
          </div>{/* End right column */}
          </div>{/* End grid */}

          {/* Featured products - mobile only (desktop shows them in right column) — fallback when no curated suggestions */}
          {suggestedItems.length === 0 && !discount.is_free_delivery && featuredProducts.length > 0 && !isDiscountExpired && (
            <div className="mt-6 rounded-2xl bg-white p-5 shadow-lg lg:hidden">
              <div className="mb-4 text-sm font-semibold text-gray-700">Mirá cuánto ahorrás</div>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                {featuredProducts.map((product) => {
                  const discountedPrice = product.price * (1 - discount.discount / 100);
                  return (
                    <div key={product.id} className="flex flex-col rounded-lg border border-gray-200 bg-white p-2.5">
                      <div className="mx-auto mb-2">
                        {product.has_image ? (
                          <img
                            src={product.webp_image_url}
                            alt={product.title}
                            loading="lazy"
                            width={80}
                            height={80}
                            className="aspect-square w-[70px] object-contain sm:w-[80px]"
                          />
                        ) : (
                          <div className="flex aspect-square w-[70px] items-center justify-center rounded bg-gray-100 sm:w-[80px]">
                            <i className="fa-solid fa-leaf text-xl text-gray-300" />
                          </div>
                        )}
                      </div>
                      <div className="mb-1 line-clamp-2 text-[11px] font-medium leading-tight text-gray-700 sm:text-xs">
                        {product.title}
                      </div>
                      <div className="mt-auto">
                        <div className="text-[11px] text-gray-400 line-through">${formatPrice(product.price)}</div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-bold text-green-600">${formatPrice(discountedPrice)}</span>
                          <span className="rounded bg-green-100 px-1 py-0.5 text-[10px] font-semibold text-green-700">
                            -{discount.discount}%
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Sticky mobile bar */}
      {!showOverlay && (
        <div className="fixed bottom-0 left-0 right-0 z-[100] border-t border-gray-200 bg-white shadow-[0_-4px_20px_rgba(0,0,0,0.1)] lg:hidden">
          <div className="px-4 pt-3" style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}>
            {isDiscountExpired ? (
              <Link
                href={route('products.index')}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#1EAA25] py-3.5 text-base font-semibold text-white transition-colors hover:opacity-90 hover:text-white hover:no-underline"
              >
                <CartIcon /> Explorar productos
              </Link>
            ) : isAlreadyApplied ? (
              <>
                <p className="mb-2 flex items-center justify-center gap-1.5 text-sm font-medium text-green-700">
                  <CheckIcon /> Descuento aplicado
                </p>
                <div className="flex gap-2">
                  <Link
                    href={route('products.index')}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#1EAA25] py-3 text-sm font-semibold text-white transition-colors hover:opacity-90 hover:text-white hover:no-underline"
                  >
                    <CartIcon /> Ver productos
                  </Link>
                  {barrioCampaignShare ? (
                    <button
                      type="button"
                      onClick={() => {
                        const text = `Yo pido verdura y fruta por acá y me llega a ${discount.neighborhood}. Si querés probar, con este link tenés ${discount.discount}% off:\n${barrioCampaignShare.shareUrl}`;
                        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                      }}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#25D366] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#20bd5a]"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                        <path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.553 4.12 1.52 5.855L.06 23.498l5.791-1.52A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-1.97 0-3.837-.53-5.445-1.454l-.39-.232-3.436.902.917-3.349-.254-.403A9.694 9.694 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75S21.75 6.615 21.75 12s-4.365 9.75-9.75 9.75z" />
                      </svg>
                      Compartir
                    </button>
                  ) : !discount.per_user && (
                    <button
                      type="button"
                      onClick={handleShare}
                      className={`flex flex-1 items-center justify-center gap-2 rounded-lg border-2 py-3 text-sm font-medium transition-colors ${
                        copied ? 'border-green-500 bg-green-50 text-green-600' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {copied ? '¡Copiado!' : 'Compartir'}
                    </button>
                  )}
                </div>
                {barrioCampaignShare && (
                  <p className="mt-2 text-center text-[11px] text-gray-400">
                    🎁 Por cada vecino que pida, ganás ${barrioCampaignShare.rewardAmount.toLocaleString('es-AR')} de crédito
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="mb-2 text-center text-[12px] text-gray-400">
                  Al aplicar el descuento, se guardará automáticamente para tu próxima compra.
                </p>
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={isApplying}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#1EAA25] py-3.5 text-base font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isApplying ? (<><SpinnerIcon /> Aplicando...</>) : (<><CheckIcon /> Aplicar {Math.round(Number(discount.discount))}% de descuento</>)}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Success Overlay */}
      <div
        className={`fixed inset-0 z-[9998] flex items-center justify-center bg-white/85 backdrop-blur-sm transition-all duration-500 ${
          showOverlay ? 'visible opacity-100' : 'invisible opacity-0'
        }`}
      >
        <div
          className={`px-6 py-10 text-center transition-transform duration-400 ${
            showOverlay ? 'scale-100' : 'scale-75'
          }`}
          style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
        >
          <div
            className="mx-auto mb-6 flex h-[100px] w-[100px] items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-green-600"
            style={{
              animation: showOverlay ? 'pop-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both' : 'none',
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              className="h-[50px] w-[50px] text-white"
              style={{
                strokeDasharray: 30,
                strokeDashoffset: showOverlay ? 0 : 30,
                transition: 'stroke-dashoffset 0.5s ease 0.5s',
              }}
            >
              <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <h2
            className="mb-3 text-[28px] font-bold text-green-800"
            style={{ animation: showOverlay ? 'fade-up 0.4s ease 0.3s both' : 'none' }}
          >
            {discount.is_free_delivery ? '¡Envío gratis aplicado!' : '¡Descuento aplicado!'}
          </h2>
          <p
            className="mb-8 text-base text-gray-500"
            style={{ animation: showOverlay ? 'fade-up 0.4s ease 0.4s both' : 'none' }}
          >
            {discount.is_free_delivery
              ? 'Tu envío gratis está listo para usar en tu próxima compra'
              : cartItemCount > 0
                ? `Tu ${discount.discount}% de descuento está listo y cargamos ${cartItemCount} productos en tu carrito`
                : `Tu ${discount.discount}% de descuento está listo para usar`}
          </p>

          <div style={{ animation: showOverlay ? 'fade-up 0.4s ease 0.5s both' : 'none' }}>
            {cartItemCount > 0 ? (
              <Link
                href={route('cart.review')}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#1EAA25] px-7 py-3.5 text-base font-semibold text-white transition-all hover:opacity-90 hover:text-white hover:no-underline"
              >
                <CartIcon />
                Ver carrito sugerido
              </Link>
            ) : (
              <Link
                href={route('products.index')}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#1EAA25] px-7 py-3.5 text-base font-semibold text-white transition-all hover:opacity-90 hover:text-white hover:no-underline"
              >
                <CartIcon />
                Ver productos
              </Link>
            )}
          </div>

          {barrioCampaignShare && (
            <div
              className="mx-auto mt-6 max-w-sm"
              style={{ animation: showOverlay ? 'fade-up 0.4s ease 0.7s both' : 'none' }}
            >
              <ReferralShareSection share={barrioCampaignShare} discount={discount} refCopied={refCopied} setRefCopied={setRefCopied} />
            </div>
          )}
        </div>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes pop-in {
          0% { transform: scale(0); }
          100% { transform: scale(1); }
        }
        @keyframes fade-up {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes rain-fall {
          0% { transform: translateY(-5vh) translateX(0); }
          100% { transform: translateY(105vh) translateX(6px); }
        }
        @keyframes expired-text-in {
          0% { opacity: 0; transform: translateY(15px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Expired intro overlay */}
      {showExpiredIntro && (
        <ExpiredIntro phase={expiredIntroPhase} />
      )}
    </MainLayout>
  );
}

// ── Expired Intro Animation ──

const RAIN_DROPS = Array.from({ length: 50 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  delay: Math.random() * 1.5,
  duration: 0.4 + Math.random() * 0.3,
  opacity: 0.1 + Math.random() * 0.15,
  height: 15 + Math.random() * 15,
}));

function ExpiredIntro({ phase }: { phase: 'rain' | 'text' | 'fade' }) {
  return (
    <div
      className={`fixed inset-0 z-[9999] bg-cover bg-center transition-opacity duration-700 ${
        phase === 'fade' ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ backgroundImage: 'url(/img/expired-promo-bg.webp)' }}
    >
      {/* Rain overlay */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {RAIN_DROPS.map((drop) => (
          <div
            key={drop.id}
            className="absolute top-0"
            style={{
              left: `${drop.x}%`,
              width: '1px',
              height: `${drop.height}px`,
              opacity: drop.opacity,
              background: 'linear-gradient(180deg, transparent, rgba(200,220,255,0.7), transparent)',
              animation: `rain-fall ${drop.duration}s linear ${drop.delay}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Text content - positioned left */}
      <div className="relative flex h-full flex-col items-center justify-center px-6">
        <div
          className={`text-center transition-all duration-700 ${
            phase === 'rain' ? 'translate-y-3 opacity-0' : 'translate-y-0 opacity-100'
          }`}
        >
          <h2
            className="text-[32px] font-bold text-white md:text-[48px]"
            style={{
              animation: phase !== 'rain' ? 'expired-text-in 0.7s ease both' : 'none',
              textShadow: '0 2px 40px rgba(0,0,0,0.8), 0 0 80px rgba(0,0,0,0.4)',
            }}
          >
            Llegaste tarde...
          </h2>
          <p
            className="mt-3 text-[16px] text-white/70 md:text-[18px]"
            style={{
              animation: phase !== 'rain' ? 'expired-text-in 0.7s ease 0.2s both' : 'none',
              textShadow: '0 1px 20px rgba(0,0,0,0.8)',
            }}
          >
            La promo que buscabas ya expiró
          </p>
          <p
            className="mt-1.5 text-[14px] text-white/50 md:text-[15px]"
            style={{
              animation: phase !== 'rain' ? 'expired-text-in 0.7s ease 0.35s both' : 'none',
              textShadow: '0 1px 20px rgba(0,0,0,0.8)',
            }}
          >
            Tal vez la próxima tengas más suerte...
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Referral Share Section (reused in desktop + mobile CTA areas) ──

function ReferralShareSection({
  share,
  discount,
  refCopied,
  setRefCopied,
}: {
  share: BarrioCampaignShare;
  discount: DiscountData;
  refCopied: boolean;
  setRefCopied: (v: boolean) => void;
}) {
  return (
    <div className="rounded-2xl bg-white p-5 shadow-lg lg:p-6">
      <div className="flex items-start gap-3 sm:items-center">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-lg">
          🎁
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-800">
            Compartí y ganá ${share.rewardAmount.toLocaleString('es-AR')} de crédito
          </p>
          <p className="text-xs text-gray-500">
            Por cada vecino que pida con tu link
          </p>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => {
            const text = `Yo pido verdura y fruta por acá y me llega a ${discount.neighborhood}. Si querés probar, con este link tenés ${discount.discount}% off:\n${share.shareUrl}`;
            window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
          }}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#20bd5a]"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.553 4.12 1.52 5.855L.06 23.498l5.791-1.52A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-1.97 0-3.837-.53-5.445-1.454l-.39-.232-3.436.902.917-3.349-.254-.403A9.694 9.694 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75S21.75 6.615 21.75 12s-4.365 9.75-9.75 9.75z" />
          </svg>
          WhatsApp
        </button>
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(share.shareUrl);
              setRefCopied(true);
              setTimeout(() => setRefCopied(false), 2000);
            } catch {
              alert(share.shareUrl);
            }
          }}
          className={`flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-4 py-2.5 text-sm font-medium transition-all ${
            refCopied
              ? 'border-green-500 bg-green-50 text-green-600'
              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
          }`}
        >
          {refCopied ? '¡Copiado!' : 'Copiar link'}
        </button>
      </div>
    </div>
  );
}
